import React, { useEffect } from "react";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { ExtractModes, IExtract } from "../../LeftPanel/ExtractAdd/redux/Types";
import { IRootState } from "../../../reducer/CombineReducer";
import { onFieldChange } from "../../LeftPanel/ExtractAdd/script";
import { ExtractAddSlice } from "../../LeftPanel/ExtractAdd";
import { Checkbox, Form, FormElement, FormSection, IListItem, Input, Textarea } from "../../Common/UI/Index";
import { InputType } from "../../Common/UI/Types";
import { ExtractMetadataKey } from "./Constant";
import "./Style.scss";

export interface IExtractAddViewProp {
  /**
   * Current extract details
   */
  newExtract: IExtract;

  /**
   * Flag to toggle Add/Edit view
   */
  isEdit?: boolean;

  /**
   * Data Values for year range dropdown
   */
  yearsDataValue: IListItem[];
}

type Props = IExtractAddViewProp;

/**
 * Responsible for rendering the add or edit extract fields
 * @returns - Returns save extract footer page
 */
const ExtractEditView = (prop: Props): JSX.Element => {
  const {
    isEdit,
    newExtract
  } = prop;

  const { t: translate } = useTranslation();
  const dispatch = useDispatch();
  const [
    extractAdd,
    extractState
  ] = useSelector((state: IRootState) => [
    state.extractAdd,
    state.extractState
  ], shallowEqual);
  const mode = extractAdd.mode;
  const isEditMode = mode === ExtractModes.EDIT;
  const title = isEditMode && extractState.selectedExtract?.extractName ?
    extractAdd.editExtractDetail.title.value : extractAdd.newExtract.title.value;
  const description = isEditMode ? extractAdd.editExtractDetail.description : extractAdd.newExtract.description;
  const financialYear = isEditMode ? extractAdd.editExtractDetail.financialYear : extractAdd.newExtract.financialYear;
  const disabledFields = extractAdd.mode === ExtractModes.VIEW;
  /**
   * Set current year by default in redux state
   */
  useEffect(() => {
    if (financialYear) {
      dispatch(ExtractAddSlice.actions.setNewExtract({
        ...extractAdd.newExtract,
        financialYear: financialYear,
      }));
      dispatch(ExtractAddSlice.actions.setEditExtractDetail({
        ...extractAdd.editExtractDetail,
        financialYear: financialYear,
      }));
    }
  }, [financialYear]);

  return (
    <div className="ebt-extract-edit" data-testid="extract-edit-container">
      <Form
        onChange={(fieldName: string, fieldValidityState: ValidityState, e: React.SyntheticEvent<any, Event>): void =>
        { onFieldChange(fieldName, fieldValidityState, e, dispatch, extractAdd, translate, mode); }}
        onBlur={(fieldName: string, fieldValidityState: ValidityState, e: React.SyntheticEvent<any, Event>): void =>
        { onFieldChange(fieldName, fieldValidityState, e, dispatch, extractAdd, translate, mode); }}
      >
        <FormSection>
          <FormElement fieldId="titleField" labelText={translate("Extract.ExtractAdd.Title")} mandatory={true}
            errorText={newExtract.title.errorText}>
            <Input
              id={ExtractMetadataKey.Title}
              className="ebt-extract-add_title"
              type={InputType.Text}
              name={ExtractMetadataKey.Title}
              value={title}
              data-testid="extract-title"
              invalid={newExtract.title.valid} required={true}
              disabled={disabledFields}
            />
          </FormElement>
          <FormElement fieldId="descriptionField" labelText={translate("Extract.ExtractAdd.Description")}>
            <Textarea
              className="ebt-extract-add_description"
              id={ExtractMetadataKey.Description}
              data-testid="extract-decription"
              value={description}
              name={ExtractMetadataKey.Description}
              rows={4}
              disabled={disabledFields}
            />
          </FormElement>
          {isEdit && <FormElement fieldId="isactivefield" >
            <Checkbox
              className="ebt-extract-add__is-active"
              data-testid="extract-isActive"
              id={ExtractMetadataKey.isactive}
              labelText={translate("Extract.ExtractAdd.IsActive")}
              name={ExtractMetadataKey.isactive}
              disabled={disabledFields}
            />
          </FormElement>}
        </FormSection>
      </Form>
    </div>);
};

export default ExtractEditView;
